/**
 * The counter's density, persisted per device. `DESIGN-TOKENS.md` §5.
 *
 * §5 ships both densities in v1 and `tokens.css` emits both, but until now nothing could change
 * it: `index.html` hardcoded `<body data-density="comfortable">` and no screen offered a choice,
 * so one of the two shipped unreachable. A token with no way to reach it is not a feature.
 *
 * **Per device is provisional and is the smallest thing that satisfies §5.** Where the setting
 * really belongs is an open product call (`later_todo.md` entry 11): per device suits a counter
 * tablet three people share across a shift, per staff member suits a person with poor near vision
 * who wants it wherever they sign in, and per tenant is one answer for the whole shop. They are
 * different products and the pilot decides. Local storage is the one that needs no API and no
 * migration to replace, so it is the cheapest thing to be wrong about.
 *
 * It goes on `<body>`, not `<html>`, because that is where `index.html` puts it and because
 * `tokens.css` scopes density with a bare `[data-density]` that any element matches: a value set
 * above body is shadowed by body's own for the whole page, which cost an hour once already
 * (`PROGRESS.md` 190).
 */

export const DENSITIES = ['compact', 'comfortable'] as const
export type Density = (typeof DENSITIES)[number]

/** What `index.html` ships, and what a device with no stored preference gets. */
export const DEFAULT_DENSITY: Density = 'comfortable'

const STORAGE_KEY = 'lensmira.density'

const isDensity = (value: unknown): value is Density =>
  typeof value === 'string' && (DENSITIES as readonly string[]).includes(value)

/**
 * Every access is wrapped, on the same reasoning `session.ts` gives: a private window or a browser
 * set to block site data throws on the accessor itself rather than returning null. A shop that
 * cannot store a preference should still get a counter.
 */
export function loadDensity(): Density {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return isDensity(stored) ? stored : DEFAULT_DENSITY
  } catch {
    return DEFAULT_DENSITY
  }
}

export function storeDensity(density: Density): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, density)
  } catch {
    // Refused. The choice still holds for this tab, which is better than refusing to apply it.
  }
}

/** Applies it where `tokens.css` reads it. Safe to call before React has mounted anything. */
export function applyDensity(density: Density): void {
  document.body.setAttribute('data-density', density)
}
